import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChild,
  ElementRef,
  HostListener,
  Input,
  NgZone,
  TemplateRef,
} from '@angular/core';
import {
  BaseChartComponent,
  calculateViewDimensions,
  ColorHelper,
  getUniqueXDomainValues,
  ViewDimensions,
} from '@swimlane/ngx-charts';
import { scaleLinear, scaleTime } from 'd3-scale';
import { curveLinear } from 'd3-shape';
import * as moment from 'moment';
import * as R from 'remeda';
import { FracStorage, StockSnapshot } from '~lmo/models/frac.model';

const lineColor = '#4d94ff';
const areaColor = '#e6f0ff';
const nptLineColor = '#ff1a1a';
const nptAreaColor = '#ff9999';
const truckYLevelPercent = 20;

interface TruckData {
  timestamps: moment.Moment[];
  transform: string;
  truckCount: number;
  x: number;
  y: number;
}

interface GraphData {
  name: string;
  series: SeriesDatum[];
}

interface SeriesDatum extends StockSnapshot {
  name: Date;
  value: number;
}

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'sa-lmo-current-stock-graph',
  styleUrls: ['lmo-current-stock-graph.component.scss'],
  template: `
    <ngx-charts-chart
      [view]="[width, height]"
      [showLegend]="false"
      [activeEntries]="activeEntries"
      [animations]="animations"
      *ngIf="fracStorage && fracStorage.currentStocks && width"
    >
      <svg:defs>
        <svg:clipPath [attr.id]="clipPathId">
          <svg:rect
            [attr.width]="dims.width + 10"
            [attr.height]="dims.height + 10"
            [attr.transform]="'translate(-5, -5)'"
          />
        </svg:clipPath>
      </svg:defs>
      <svg:g [attr.transform]="transform" class="area-chart chart">
        <svg:g [attr.clip-path]="clipPath">
          <svg:g *ngFor="let series of results; trackBy: trackBy">
            <svg:g
              sa-lmo-current-stock-graph-custom-line
              [xScale]="xScale"
              [yScale]="yScale"
              [colors]="lineColor"
              [data]="series"
              [activeEntries]="activeEntries"
              [curve]="curve"
              [animations]="animations"
            />
          </svg:g>
          <line
            *ngIf="capcacityYPosition !== 0"
            class="capacity-line"
            [attr.x1]="0"
            [attr.y1]="capcacityYPosition"
            [attr.x2]="dims.width"
            [attr.y2]="capcacityYPosition"
          />
          <svg:g>
            <text class="graph-label axis-label" text-anchor="center" [attr.transform]="yAxisTransform">
              stock
            </text>
            <text class="graph-label axis-label" text-anchor="center" [attr.transform]="xAxisTransform">
              future →
            </text>
            <text class="graph-label" text-anchor="center" [attr.transform]="maxStockLableTransform">
              {{ fracStorage.capacity | approxBigNumber }} Max
            </text>
          </svg:g>
          <svg:g *ngFor="let truck of truckData; trackBy: trackBy">
            <svg:g [attr.transform]="truck.transform">
              <svg:g
                sa-lmo-current-stock-graph-truck
                class="circle"
                [truckCount]="truck.truckCount"
                [isActive]="isActiveTruck(truck)"
              />
            </svg:g>
          </svg:g>
          <svg:g *ngIf="!tooltipDisabled" (mouseleave)="hideCircles()">
            <svg:g
              sa-lmo-charts-tooltip-area
              [dims]="dims"
              [xScale]="xScale"
              [yScale]="yScale"
              [results]="fracStorage.currentStocks"
              [tooltipTemplate]="seriesTooltipTemplate"
              (hover)="updateHoveredVertical($event)"
            />
          </svg:g>
        </svg:g>
      </svg:g>
    </ngx-charts-chart>
  `,
})
export class LmoCurrentStockGraphComponent extends BaseChartComponent {
  public get xAxisTransform(): string {
    return `translate(${this.dims.width / 2 - 30}, ${this.dims.height - 10})`;
  }
  public get yAxisTransform(): string {
    return `translate(25, ${this.dims.height / 2 + 15})rotate(-90)`;
  }

  public get maxStockLableTransform(): string {
    return `translate(${this.dims.width - 110}, ${this.capcacityYPosition + 20})`;
  }
  private _fracStorage: FracStorage;
  public truckData: TruckData[] = [];

  public get fracStorage(): FracStorage {
    return this._fracStorage;
  }
  @Input() public set fracStorage(fracStorage: FracStorage) {
    if (!fracStorage || !fracStorage.currentStocks) {
      return;
    }
    const sortedStorage = {
      ...fracStorage,
      currentStocks: fracStorage.currentStocks.sort((a, b) => (a.timestamp < b.timestamp ? -1 : 1)),
    };
    this._fracStorage = sortedStorage;
    const nonNptGraph = getNonNptData(this._fracStorage);
    const nptGraph = getNptData(this._fracStorage);
    this.results = [nonNptGraph, nptGraph];
  }

  @ContentChild('tooltipTemplate', { static: true }) public tooltipTemplate: TemplateRef<any>;
  @ContentChild('seriesTooltipTemplate', { static: true })
  public seriesTooltipTemplate: TemplateRef<any>;

  public tooltipDisabled = false;
  public yScaleMin: number;
  public yScaleMax: number;
  public xScaleMin: any;
  public xScaleMax: any;
  public curve: any = curveLinear;
  public activeEntries: any[] = [];
  public dims: ViewDimensions;
  public xDomain: any;
  public yDomain: any;
  public seriesDomain: any;
  public xScale: any;
  public yScale: any;
  public transform: string;
  public lineColor: ColorHelper;
  public areaColor: ColorHelper;
  public clipPathId: string;
  public clipPath: string;
  public scaleType = 'time';
  public series: any;
  public margin = [0, 0, 0, -2]; // top, left, bottom, right
  public hoveredVertical: any; // the value of the x axis that is hovered over
  public xAxisHeight = 0;
  public yAxisWidth = 0;
  public filteredDomain: any;

  public timelineWidth: any;
  public timelineHeight = 50;
  public timelineXScale: any;
  public timelineYScale: any;
  public timelineXDomain: any;
  public timelineTransform: any;
  public timelinePadding = 10;
  public capcacityYPosition;

  constructor(protected chartElement: ElementRef, protected zone: NgZone, protected cd: ChangeDetectorRef) {
    super(chartElement, zone, cd);
  }

  public update(): void {
    // Because we are so async, we need to wait a tick for the rectangle to be set
    this.checkAndSetDimensionsOnceMore().then(() => {
      if (!this.results || !this.results.length) {
        return;
      }
      this.dims = calculateViewDimensions({
        height: this.height,
        legendType: this.schemeType,
        margins: this.margin,
        showLegend: false,
        showXAxis: false,
        showXLabel: false,
        showYAxis: false,
        showYLabel: false,
        width: this.width,
        xAxisHeight: this.xAxisHeight,
        yAxisWidth: this.yAxisWidth,
      });

      this.xDomain = this.getXDomain();
      if (this.filteredDomain) {
        this.xDomain = this.filteredDomain;
      }

      this.yDomain = this.getYDomain();
      this.seriesDomain = this.getSeriesDomain();

      this.xScale = this.getXScale(this.xDomain, this.dims.width);
      this.yScale = this.getYScale(this.yDomain, this.dims.height);
      this.capcacityYPosition = this.yScale(this.fracStorage ? this.fracStorage.capacity : 0);

      this.setColors();

      this.transform = `translate(${this.dims.xOffset}, ${this.margin[0]})`;

      this.clipPathId = 'clip' + id().toString();
      this.clipPath = `url(#${this.clipPathId})`;

      this.setupTruckData();
      this.cd.markForCheck();
    });
  }
  private checkAndSetDimensionsOnceMore(): Promise<void> {
    return Promise.resolve().then(() => {
      const hostElem = this.chartElement.nativeElement;

      if (hostElem.parentNode !== null) {
        // Get the container dimensions
        const dims = hostElem.parentNode.getBoundingClientRect();
        this.width = dims.width;
        this.height = dims.height;
      }
    });
  }

  public getXDomain(): any[] {
    const values = getUniqueXDomainValues(this.results);

    let domain = [];

    const min = this.xScaleMin ? this.xScaleMin : Math.min(...values);
    const max = this.xScaleMax ? this.xScaleMax : Math.max(...values);

    domain = [
      moment(min)
        .subtract('15', 'minutes')
        .toDate(),
      moment(max)
        .add('15', 'minutes')
        .toDate(),
    ];
    return domain;
  }

  public getYDomain(): any[] {
    if (!this.fracStorage || !this.fracStorage.currentStocks) {
      return [0, 0];
    }
    const stocks = this.fracStorage.currentStocks.map((currentStock) => currentStock.stock);
    const maxYValue = Math.max(...stocks, this.fracStorage.capacity);
    return [0, maxYValue];
  }

  public getSeriesDomain(): any[] {
    return this.results.map((d) => d.name);
  }

  public getXScale(domain, width): any {
    return scaleTime()
      .range([0, width])
      .domain(domain);
  }

  public getYScale(domain, height): any {
    return scaleLinear()
      .range([height, 0])
      .domain(domain);
  }

  public updateDomain(domain): void {
    this.filteredDomain = domain;
    this.xDomain = this.filteredDomain;
    this.xScale = this.getXScale(this.xDomain, this.dims.width);
  }

  public updateHoveredVertical(item): void {
    this.hoveredVertical = item.value;
    this.deactivateAll();
  }

  @HostListener('mouseleave')
  public hideCircles(): void {
    this.hoveredVertical = null;
    this.deactivateAll();
  }

  public trackBy(_index, item): string {
    return item.name || item.x;
  }

  public setColors(): void {
    const domain = this.seriesDomain;
    this.lineColor = new ColorHelper({ domain: [lineColor, nptLineColor] }, this.schemeType, domain, this.customColors);
    this.areaColor = new ColorHelper({ domain: [areaColor, nptAreaColor] }, this.schemeType, domain, this.customColors);
  }

  public updateYAxisWidth({ width }): void {
    this.yAxisWidth = width;
    this.update();
  }

  public updateXAxisHeight({ height }): void {
    this.xAxisHeight = height;
    this.update();
  }

  public onActivate(item) {
    const idx = this.activeEntries.findIndex((d) => {
      return d.name === item.name && d.value === item.value;
    });
    if (idx > -1) {
      return;
    }

    this.activeEntries = [item, ...this.activeEntries];
  }

  public onDeactivate(item) {
    const idx = this.activeEntries.findIndex((d) => {
      return d.name === item.name && d.value === item.value;
    });

    this.activeEntries.splice(idx, 1);
    this.activeEntries = [...this.activeEntries];
  }

  public deactivateAll() {
    this.activeEntries = [...this.activeEntries];
    this.activeEntries = [];
  }

  public setupTruckData() {
    if (this.fracStorage && this.fracStorage.currentStocks) {
      // Skip the first one so we have an even number of groups then add it in after grouping to the first sum
      const hourLongChunks = R.chunk(this.fracStorage.currentStocks.slice(1), 2);
      const hourLongSums: { timestamps: moment.Moment[]; count: number }[] = hourLongChunks.map((chunk) => {
        return chunk.reduce(
          (accum, snapshot) => {
            accum.timestamps.push(moment(snapshot.timestamp));
            accum.count += snapshot.deliveryCountExisting;
            return accum;
          },
          { timestamps: [], count: 0 },
        );
      });
      // Add in the first data point to the first one (it covers 3 datapoints instead of just 2 like the rest)
      const nowSnapShot = this.fracStorage.currentStocks[0];
      hourLongSums[0].count += nowSnapShot.deliveryCountExisting;
      hourLongSums[0].timestamps.push(moment(nowSnapShot.timestamp));
      const bubbleXScale = scaleLinear()
        .range([0, this.dims.width])
        .domain([0, hourLongSums.length]); // Should be 14 but just in case this increases later, no hardcoding
      const bubbleYScale = this.getYScale([0, 100], this.dims.height); // Just a 100 point scale to make it easy to move trucks up and down
      this.truckData = hourLongSums
        .map((hourGroup, index) => {
          if (hourGroup.count !== 0) {
            const y = truckYLevelPercent;
            const x = index;
            const truckCount = hourGroup.count;

            const cx = bubbleXScale(x + 0.5); // Put this in the middle of the hour session
            const cy = bubbleYScale(y);
            const transform = `translate(${cx + 1},${cy})`;

            return {
              timestamps: hourGroup.timestamps,
              transform,
              truckCount,
              x,
              y,
            };
          }
        })
        .filter((truckDatum) => !!truckDatum);
    }
  }

  public isActiveTruck(truck: TruckData): boolean {
    return truck && truck.timestamps.some((m) => m.isSame(this.hoveredVertical));
  }
}

const cache = {};

/**
 * Generates a short id.
 *
 * Description:
 *   A 4-character alphanumeric sequence (364 = 1.6 million)
 *   This should only be used for JavaScript specific models.
 *   http://stackoverflow.com/questions/6248666/how-to-generate-short-uid-like-ax4j9z-in-js
 *
 *   Example: `ebgf`
 */
function id(): string {
  // tslint:disable-next-line
  let newId = ('0000' + ((Math.random() * Math.pow(36, 4)) << 0).toString(36)).slice(-4);

  // append a 'a' because neo gets mad
  newId = `a${newId}`;

  // ensure not already used
  if (!cache[newId]) {
    cache[newId] = true;
    return newId;
  }

  return id();
}

function getNonNptData(fracStorage: FracStorage): GraphData {
  const nonNptGraph: GraphData = {
    name: `${fracStorage.mesh.type} OKAY`,
    series: [],
  };
  const length = fracStorage.currentStocks ? fracStorage.currentStocks.length : 0;
  for (let i = 0; i < length; i++) {
    const currentStock = fracStorage.currentStocks[i];
    if (!currentStock.onNPT) {
      // If the previous stock was NPT and this is not, we need to create another null point for the other nulls to join to right BEFORE we resume the graph
      if (i > 0) {
        const previousStock = fracStorage.currentStocks[i - 1];
        if (previousStock.onNPT) {
          nonNptGraph.series.push({
            ...previousStock,
            name: new Date(previousStock.timestamp),
            value: null,
          });
        }
      }
      nonNptGraph.series.push({
        ...currentStock,
        name: new Date(currentStock.timestamp),
        value: currentStock.stock,
      });
      if (i < fracStorage.currentStocks.length - 1) {
        const nextStock = fracStorage.currentStocks[i + 1];
        if (nextStock.onNPT) {
          nonNptGraph.series.push({
            ...nextStock,
            name: new Date(nextStock.timestamp),
            value: nextStock.stock,
          });
        }
      }
    } else {
      nonNptGraph.series.push({
        ...currentStock,
        name: new Date(currentStock.timestamp),
        value: null,
      });
    }
  }
  return nonNptGraph;
}

function getNptData(fracStorage: FracStorage): GraphData {
  const nptGraph: GraphData = {
    name: `${fracStorage.mesh.type} NPT`,
    series: [],
  };
  const length = fracStorage.currentStocks ? fracStorage.currentStocks.length : 0;
  for (let i = 0; i < length; i++) {
    const currentStock = fracStorage.currentStocks[i];
    if (currentStock.onNPT) {
      // If the previous stock was not NPT and this is, we need to create another non-npt point for the other not-npts to join to right BEFORE we resume the graph
      if (i > 0) {
        const previousStock = fracStorage.currentStocks[i - 1];
        if (!previousStock.onNPT) {
          nptGraph.series.push({
            ...previousStock,
            name: new Date(previousStock.timestamp),
            value: null,
          });
        }
      }
      nptGraph.series.push({
        ...currentStock,
        name: new Date(currentStock.timestamp),
        value: currentStock.stock,
      });
      if (i < fracStorage.currentStocks.length - 1) {
        const nextStock = fracStorage.currentStocks[i + 1];
        if (!nextStock.onNPT) {
          nptGraph.series.push({
            ...nextStock,
            name: new Date(nextStock.timestamp),
            value: nextStock.stock,
          });
        }
      }
    } else {
      nptGraph.series.push({
        ...currentStock,
        name: new Date(currentStock.timestamp),
        value: null,
      });
    }
  }
  return nptGraph;
}
